import { useState } from 'react';
import * as React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import HomeIcon from '@mui/icons-material/Home';
import {
  useRpcClientCall,
  adminRpcClient,
} from '@/providers/backendClient';

interface ReportItem {
  name: string;
  displayName?: string;
  path: string;
  type: 'file' | 'folder';
  lastModified?: string;
  size?: number;
  children?: ReportItem[];
}

interface ReportsNavigationProps {
  prefix?: string;
  onFileSelect: (filePath: string) => void;
  selectedPath?: string;
  onNavigateUp?: () => void;
  onNavigateRoot?: () => void;
}

/**
 * Sorts children: non-numbered files first, then numbered files in ascending order
 */
const sortChildren = (children: ReportItem[]): ReportItem[] =>
  [...children].sort((a, b) => {
    // Extract number prefix if present (e.g., "01-problem.md" -> 1, "README.md" -> null)
    const getNumberPrefix = (name: string): number | null => {
      const match = name.match(/^(\d+)-/);
      return match ? parseInt(match[1], 10) : null;
    };

    const aNum = getNumberPrefix(a.name);
    const bNum = getNumberPrefix(b.name);

    // Both have numbers: sort by number ascending
    if (aNum !== null && bNum !== null) {
      return aNum - bNum;
    }

    // One has number, one doesn't: non-numbered comes first
    if (aNum === null && bNum !== null) {
      return -1;
    }
    if (aNum !== null && bNum === null) {
      return 1;
    }

    // Both don't have numbers: sort alphabetically
    return a.name.localeCompare(b.name);
  });

const ReportTreeItem = ({
  item,
  level = 0,
  onFileSelect,
  selectedPath,
  onLoadChildren,
  expandedPaths,
}: {
  item: ReportItem;
  level?: number;
  onFileSelect: (filePath: string) => void;
  selectedPath?: string;
  onLoadChildren?: (path: string) => void;
  expandedPaths?: Set<string>;
}) => {
  // Auto-expand if this path is in the expandedPaths set (for selected file's parents)
  const shouldBeOpen = expandedPaths?.has(item.path) ?? level < 2;
  const [open, setOpen] = useState(shouldBeOpen);
  const [children, setChildren] = useState<ReportItem[] | undefined>(
    item.children,
  );
  const [isLoadingChildren, setIsLoadingChildren] = useState(false);

  const isSelected = selectedPath === item.path;
  const hasChildren = children && children.length > 0;
  const needsLoading =
    item.type === 'folder' && !hasChildren && !isLoadingChildren;

  // Update open state when expandedPaths changes
  React.useEffect(() => {
    if (expandedPaths?.has(item.path)) {
      setOpen(true);
    }
  }, [expandedPaths, item.path]);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (item.type === 'file') {
      onFileSelect(item.path);
      return;
    }

    // Folder clicked
    if (needsLoading && onLoadChildren) {
      // Immediately expand and show loading
      setOpen(true);
      setIsLoadingChildren(true);

      // Load children
      await onLoadChildren(item.path);
    } else if (hasChildren) {
      // Toggle open/close if already has children
      setOpen(!open);
    }
  };

  // Update children when item.children changes (from parent)
  React.useEffect(() => {
    if (item.children !== undefined) {
      setChildren(item.children);
      setIsLoadingChildren(false);
      // Auto-expand when children are loaded
      if (item.children.length > 0) {
        setOpen(true);
      }
    }
  }, [item.children]);

  return (
    <>
      <ListItem
        disablePadding
        sx={{
          pl: level * 2,
          backgroundColor: isSelected
            ? 'action.selected'
            : 'transparent',
        }}
      >
        <ListItemButton onClick={handleClick}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            {item.type === 'folder' ? (
              hasChildren ? (
                open ? (
                  <FolderOpenIcon fontSize="small" />
                ) : (
                  <FolderIcon fontSize="small" />
                )
              ) : (
                <FolderIcon fontSize="small" />
              )
            ) : (
              <DescriptionIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={item.displayName || item.name}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: isSelected ? 600 : 400,
            }}
          />
          {item.type === 'folder' && (
            <Box sx={{ ml: 1 }}>
              {isLoadingChildren ? (
                <CircularProgress size={16} />
              ) : open ? (
                <ExpandMore fontSize="small" />
              ) : (
                <ExpandLess fontSize="small" />
              )}
            </Box>
          )}
        </ListItemButton>
      </ListItem>
      {item.type === 'folder' && open && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {isLoadingChildren ? (
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress size={20} />
              </Box>
            ) : hasChildren ? (
              sortChildren(children!).map(child => (
                <ReportTreeItem
                  key={child.path}
                  item={child}
                  level={level + 1}
                  onFileSelect={onFileSelect}
                  selectedPath={selectedPath}
                  onLoadChildren={onLoadChildren}
                  expandedPaths={expandedPaths}
                />
              ))
            ) : null}
          </List>
        </Collapse>
      )}
    </>
  );
};

export const ReportsNavigation = ({
  prefix,
  onFileSelect,
  selectedPath,
  onNavigateUp,
  onNavigateRoot,
}: ReportsNavigationProps) => {
  const [items, setItems] = useState<ReportItem[]>([]);
  const { data, isLoading, error } = useRpcClientCall(
    'PUBLIC_ADMIN_reports_list',
    [prefix || ''],
    {},
  );

  // Calculate which paths should be expanded based on selectedPath
  const expandedPaths = React.useMemo(() => {
    const paths = new Set<string>();
    if (selectedPath) {
      // Extract all parent folder paths
      const parts = selectedPath.split('/');
      let currentPath = '';
      for (let i = 0; i < parts.length - 1; i++) {
        currentPath = currentPath ? `${currentPath}/${parts[i]}` : parts[i];
        paths.add(currentPath);
      }
    }
    return paths;
  }, [selectedPath]);

  // Update items when data changes
  React.useEffect(() => {
    const result = data as
      | { success?: boolean; items?: ReportItem[] }
      | undefined;
    if (result?.success && result?.items) {
      setItems(result.items);
    }
  }, [data]);

  // Auto-expand and load parent folders when selectedPath changes
  React.useEffect(() => {
    if (selectedPath && expandedPaths.size > 0) {
      // Load children for all parent folders that need to be expanded
      const loadParentFolders = async () => {
        for (const path of expandedPaths) {
          // Check if this path's children are already loaded
          const findItem = (items: ReportItem[], targetPath: string): ReportItem | null => {
            for (const item of items) {
              if (item.path === targetPath) {
                return item;
              }
              if (item.children) {
                const found = findItem(item.children, targetPath);
                if (found) return found;
              }
            }
            return null;
          };

          setItems(prevItems => {
            const item = findItem(prevItems, path);
            if (item && item.type === 'folder' && (!item.children || item.children.length === 0)) {
              // Load children asynchronously
              adminRpcClient(
                'PUBLIC_ADMIN_reports_list',
                [path],
                {},
              ).then((result) => {
                const r = result as { success: boolean; items?: ReportItem[] };
                if (r.success && r.items) {
                  const updateItemsWithChildren = (
                    items: ReportItem[],
                  ): ReportItem[] =>
                    items.map(item => {
                      if (item.path === path) {
                        return { ...item, children: r.items };
                      }
                      if (item.children) {
                        return {
                          ...item,
                          children: updateItemsWithChildren(item.children),
                        };
                      }
                      return item;
                    });
                  setItems(prevItems => updateItemsWithChildren(prevItems));
                }
              }).catch((error) => {
                console.error('Error loading parent folder:', error);
              });
            }
            return prevItems;
          });
        }
      };

      loadParentFolders();
    }
  }, [selectedPath, expandedPaths]);

  const handleLoadChildren = async (folderPath: string) => {
    try {
      // Make RPC call to load children using the RPC client
      const result = (await adminRpcClient(
        'PUBLIC_ADMIN_reports_list',
        [folderPath],
        {},
      )) as { success: boolean; items?: ReportItem[] };

      if (result.success && result.items) {
        // Update the items state to include children for this folder
        const updateItemsWithChildren = (
          items: ReportItem[],
        ): ReportItem[] =>
          items.map(item => {
            if (item.path === folderPath) {
              return { ...item, children: result.items };
            }
            if (item.children) {
              return {
                ...item,
                children: updateItemsWithChildren(item.children),
              };
            }
            return item;
          });
        setItems(updateItemsWithChildren(items));
      }
    } catch (error) {
      console.error('Error loading children:', error);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load reports:{' '}
        {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    );
  }

  const dataResult = data as
    | { success?: boolean; items?: ReportItem[] }
    | undefined;
  if (!dataResult?.success || items.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No reports found
        </Typography>
      </Box>
    );
  }

  // Sort root items: folders by date (descending for most recent first), then files
  const sortedItems = [...items].sort((a, b) => {
    // Extract date from folder names (YYYY-MM-DD-{number}-{name})
    const getFolderDate = (name: string): string | null => {
      const match = name.match(/^(\d{4}-\d{2}-\d{2})-/);
      return match ? match[1] : null;
    };

    const aDate = getFolderDate(a.name);
    const bDate = getFolderDate(b.name);

    // Both are folders with dates: sort by date descending (most recent first)
    if (aDate !== null && bDate !== null) {
      // Compare dates as strings (YYYY-MM-DD format sorts correctly)
      if (aDate !== bDate) {
        return bDate.localeCompare(aDate); // Descending (newest first)
      }
      // Same date, extract number for secondary sort
      const getFolderNumber = (name: string): number | null => {
        const match = name.match(/^\d{4}-\d{2}-\d{2}-(\d+)-/);
        return match ? parseInt(match[1], 10) : null;
      };
      const aNum = getFolderNumber(a.name);
      const bNum = getFolderNumber(b.name);
      if (aNum !== null && bNum !== null) {
        return bNum - aNum; // Descending (higher number first)
      }
    }

    // Sort alphabetically for others
    return a.name.localeCompare(b.name);
  });

  return (
    <Box>
      {(onNavigateUp || onNavigateRoot) && (
        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
          {onNavigateRoot && (
            <Button
              size="small"
              startIcon={<HomeIcon />}
              onClick={onNavigateRoot}
              sx={{ mr: 1 }}
              variant="outlined"
            >
              Root
            </Button>
          )}
          {onNavigateUp && prefix && (
            <Button
              size="small"
              startIcon={<ArrowUpwardIcon />}
              onClick={onNavigateUp}
              variant="outlined"
            >
              Up
            </Button>
          )}
        </Box>
      )}
      <List dense sx={{ width: '100%', bgcolor: 'background.paper' }}>
        {sortedItems.map(item => (
          <ReportTreeItem
            key={item.path}
            item={item}
            onFileSelect={onFileSelect}
            selectedPath={selectedPath}
            onLoadChildren={handleLoadChildren}
            expandedPaths={expandedPaths}
          />
        ))}
      </List>
    </Box>
  );
};
